import {RemoveItemsList} from "components/RemoveItemsList/RemoveItemsList";
import {KeyValueOption} from "components/EditList/ExtendedKeyValueEditList";
import {BaseComponent} from "components/BaseComponent/BaseComponent";
import {ActionEditProps} from "components/Actions/pluginRegistry";
import {KubernetesIngressProperties} from "components/Actions/kubernetes/kubernetesProperties";
import {JsonUtils} from "utils/jsonUtils";
import DialogOpener from "components/Dialog/DialogOpener";
import IngressDialog from "components/Actions/kubernetes/ingressRuleDialog";
import {default as ExpandableFormSection} from "components/form/Sections/ExpandableFormSection";
import {VariableLookupText} from "components/form/VariableLookupText";
import ActionButton from "components/Button";
import StringExtendedKeyValueEditList from "components/EditList/ExtendedKeyValueEditList";
import Note from "components/form/Note/Note";
import {clone} from "lodash";
import Summary from "components/form/Sections/Summary";
import {IngressAnnotations} from "components/Features/kubernetes/ingressAnnotationsSuggestions";
import * as React from "react";
import _ = require("lodash");
import ActionProperties from "client/resources/actionProperties";
import {PackageReferenceProperties} from "client/resources/packageReference";
import ExternalLink from "components/Navigation/ExternalLink/ExternalLink";

interface KubernetesDeploymentState {
    ingressRules: IngressRule[];
    editIngressRule: IngressRule;
    editIngressRuleIndex: number;
}

class IngressList extends RemoveItemsList<IngressRule> {
}

export interface IngressRule {
    host: string;
    http: {
        paths: KeyValueOption[];
    };
}

export interface KubernetesIngressComponentProperties<T = ActionProperties, P = PackageReferenceProperties> extends ActionEditProps<T, P> {
    standAlone: boolean;
}

export class KubernetesIngressComponent extends BaseComponent<KubernetesIngressComponentProperties<KubernetesIngressProperties>, KubernetesDeploymentState> {
    constructor(props: KubernetesIngressComponentProperties<KubernetesIngressProperties>) {
        super(props);
        this.state = {
            ingressRules: [],
            editIngressRule: null,
            editIngressRuleIndex: null
        };
    }

    componentDidMount() {
        this.props.doBusyTask(async () => {
            this.setState({
                ingressRules: JsonUtils.tryParseArray(this.props.properties["Octopus.Action.KubernetesContainers.IngressRules"], [])
            });

            // Sanitise the ingress list, which might have been an object before the input control was switched
            if (JsonUtils.tryParseArray(this.props.properties["Octopus.Action.KubernetesContainers.IngressAnnotations"], []).length === 0) {
                this.props.setProperties({["Octopus.Action.KubernetesContainers.IngressAnnotations"]: "[]"}, true);
            }
        });
    }

    componentWillReceiveProps(nextProps: ActionEditProps<KubernetesIngressProperties>) {
        if (this.props.properties["Octopus.Action.KubernetesContainers.IngressRules"] !==
            nextProps.properties["Octopus.Action.KubernetesContainers.IngressRules"]) {
            this.setState({ingressRules: JsonUtils.tryParseArray(nextProps.properties["Octopus.Action.KubernetesContainers.IngressRules"], [])});
        }
    }

    render() {
        const editIngressDialog = <DialogOpener open={!!this.state.editIngressRule} onClose={this.resetIngressRule}>
            <IngressDialog
                ingressRule={this.state.editIngressRule}
                projectId={this.props.projectId}
                doBusyTask={this.props.doBusyTask}
                localNames={this.props.localNames}
                onAdd={item => this.saveIngressRule(item)}
                servicePorts={JsonUtils.tryParseArray(this.props.properties["Octopus.Action.KubernetesContainers.ServicePorts"], [])}
                standAlone={this.props.standAlone}
            />
        </DialogOpener>;

        return <div>
            {editIngressDialog}
            <ExpandableFormSection
                errorKey="Octopus.Action.KubernetesContainers.IngressName"
                isExpandedByDefault={this.props.expandedByDefault}
                title="Ingress Name"
                summary={this.ingressSummary()}
                help={"Enter the service name exposing the deployment."}>
                <Note>
                    The name of the ingress resource.
                </Note>
                <Note>
                    Learn more about <ExternalLink href="https://octopus.com/docs/deployment-examples/kubernetes-deployments/deploy-container#ingress-name">ingress name</ExternalLink>.
                </Note>
                <VariableLookupText
                    localNames={this.props.localNames}
                    projectId={this.props.projectId}
                    value={this.props.properties["Octopus.Action.KubernetesContainers.IngressName"]}
                    onChange={(x) => this.props.setProperties({ ["Octopus.Action.KubernetesContainers.IngressName"]: x })}
                    error={this.props.getFieldError("Octopus.Action.KubernetesContainers.IngressName")}
                    label="Ingress name" />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="Octopus.Action.KubernetesContainers.IngressAnnotations"
                isExpandedByDefault={this.props.expandedByDefault}
                title="Ingress Annotations"
                summary={this.ingressAnnotationsSummary()}
                help={"Add annotations to configure the ingress controller."}>
                <Note>
                    Ingress annotations can be specific to the type of ingress controller used by the Kubernetes cluster, and the suggested annotation keys are not exhaustive.
                </Note>
                <Note>
                    Learn more about <ExternalLink href="https://octopus.com/docs/deployment-examples/kubernetes-deployments/deploy-container#ingress-annotations">ingress annotations</ExternalLink>.
                </Note>
                <StringExtendedKeyValueEditList
                    items={this.props.properties["Octopus.Action.KubernetesContainers.IngressAnnotations"]}
                    name="Annotation"
                    onChange={val => this.props.setProperties({ ["Octopus.Action.KubernetesContainers.IngressAnnotations"]: val })}
                    valueLabel="Value"
                    keyLabel="Name"
                    getOptions={this.getIngressAnnotations}
                    hideBindOnKey={false}
                    projectId={this.props.projectId}
                    addToTop={true}
                />

            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="Octopus.Action.KubernetesContainers.IngressRules"
                isExpandedByDefault={this.props.expandedByDefault}
                title="Ingress Host Rules"
                summary={this.ingressRulesSummary()}
                help={"Add rules to be applied to hosts."}>
                <Note>
                    Host rules map incoming requests by their host name and path to ports exposed by the service resource.
                </Note>
                <Note>
                    Learn more about <ExternalLink href="https://octopus.com/docs/deployment-examples/kubernetes-deployments/deploy-container#ingress-host-rules">ingress host rules</ExternalLink>.
                </Note>
                <IngressList
                    listActions={[<ActionButton key="add" label="Add Host Rule" onClick={() => this.addIngressRule()}/>]}
                    data={this.state.ingressRules}
                    onRow={(binding) => <div>
                        {binding.http.paths
                            ? <div>
                                <ul>{binding.http.paths.map(
                                    path =>
                                        <li>
                                            <strong>{binding.host || "*"}{path.key}</strong>
                                            &nbsp;=>&nbsp;
                                            <strong>
                                                {this.props.standAlone
                                                    ? <span>{path.option}</span>
                                                    : <span>{this.props.properties["Octopus.Action.KubernetesContainers.ServiceName"]}</span>}:{path.value}
                                            </strong>
                                        </li>)}
                                </ul>
                            </div>
                            : <div>No paths defined</div>}
                    </div>}
                    onRowTouch={(binding) => this.editIngressRule(binding)}
                    onRemoveRow={(binding) => this.removeIngressRule(binding)}/>
            </ExpandableFormSection>
        </div>;
    }

    addIngressRule = () => {
        const binding: IngressRule = {
            host: "",
            http: {paths: []},
        };

        this.setState({
            editIngressRule: binding,
            editIngressRuleIndex: null
        });
    }

    editIngressRule = (binding: IngressRule) => {
        this.setState({
            editIngressRule: clone(binding),
            editIngressRuleIndex: this.state.ingressRules.indexOf(binding)
        });
    }

    removeIngressRule = (binding: IngressRule) => {
        const bindings = [...this.state.ingressRules];
        bindings.splice(this.state.ingressRules.indexOf(binding), 1);
        this.props.setProperties({["Octopus.Action.KubernetesContainers.IngressRules"]: JSON.stringify(bindings)});
    }

    resetIngressRule = () => {
        this.setState({
            editIngressRule: null,
            editIngressRuleIndex: null
        });
    }

    saveIngressRule = (binding: IngressRule) => {
        const bindings = [...this.state.ingressRules];
        if (this.state.editIngressRuleIndex === null) {
            bindings.push(binding);
        } else {
            bindings[this.state.editIngressRuleIndex] = binding;
        }
        this.props.setProperties({["Octopus.Action.KubernetesContainers.IngressRules"]: JSON.stringify(bindings)});
        this.resetIngressRule();
        return true;
    }

    private ingressRulesSummary() {
        const rules: IngressRule[] = JsonUtils.tryParseArray(this.props.properties["Octopus.Action.KubernetesContainers.IngressRules"], []);

        if (rules.length === 0) {
            return Summary.placeholder("No host rules have been included");
        }

        return Summary.summary(
            <span>Add the rule{rules.length > 1 && <span>s</span>}: {
                _.chain(rules)
                    .flatMap(rule => [
                        ..._.chain(rule.http.paths)
                            .flatMap(pair => [<span><strong>{rule.host || "*"}{pair.key}</strong> => <strong>{pair.value}</strong></span>, <span>, </span>])
                            .slice(0, -1)
                            .value(),
                        <span>; </span>])
                    .slice(0, -1)
                    .value()
            }</span>
        );
    }

    private ingressAnnotationsSummary() {
        const annotations: KeyValueOption[] = JsonUtils.tryParseArray(this.props.properties["Octopus.Action.KubernetesContainers.IngressAnnotations"], []);

        if (annotations.length === 0) {
            return Summary.placeholder("No annotations have been included");
        }

        return Summary.summary(<span>
            Add the annotation{annotations.length > 1 && <span>s</span>} {
                _.chain(annotations)
                .flatMap(annotation => [<strong>{annotation.key}: {annotation.value}</strong>, <span>, </span>])
                .slice(0, -1)
                .value()}
            </span>);
    }

    private ingressSummary() {
        if (!(this.props.properties["Octopus.Action.KubernetesContainers.IngressName"])) {
            return Summary.placeholder("No name has been provided");
        }

        return Summary.summary(<span>
            Create an ingress called <strong>{this.props.properties["Octopus.Action.KubernetesContainers.IngressName"]}</strong>
        </span>);
    }

    private getIngressAnnotations = async (searchText: string) => {
        const results = _.chain(IngressAnnotations)
            .filter(v => !!v)
            .filter(v => v.toLowerCase().includes(searchText.toLowerCase()))
            .value();
        const itemsToTake = 7;
        return {
            items: results.slice(0, itemsToTake).map(f => ({ Id: f, Name: f })),
            containsAllResults: results.length <= itemsToTake
        };
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/Actions/kubernetes/kubernetesIngressComponent.tsx